import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';

export default function PropertyDataChecker() {
  const { user } = useUser();
  const [sampleData, setSampleData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkData = async () => {
      try {
        // Obtener muestra de 5 propiedades
        const q = query(collection(db, 'propiedades'), limit(5));
        const snapshot = await getDocs(q);
        
        const datos = snapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }));
        
        setSampleData(datos);
        console.log('Muestra de propiedades:', datos);
        
      } catch (error) {
        console.error('Error obteniendo muestra:', error);
      } finally {
        setLoading(false);
      }
    };

    checkData();
  }, []);

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden ver este diagnóstico.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-warning text-white">
              <h3 className="mb-0">🔍 Diagnóstico de Estructura de Propiedades</h3>
              <small>Verificación de campos críticos en muestra de propiedades</small>
            </div>
            <div className="card-body">
              
              {sampleData.map((item, index) => (
                <div key={index} className="card mb-3 border-info">
                  <div className="card-header bg-info text-white">
                    <h6 className="mb-0">
                      Propiedad {index + 1}: {item.data.titulo || item.data.list_number || item.id}
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <h6 className="text-primary">📋 Información Básica</h6>
                        <ul className="list-unstyled small">
                          <li><strong>propiedad_id:</strong> {item.data.propiedad_id || '❌ FALTANTE'}</li>
                          <li><strong>list_number:</strong> {item.data.list_number || '❌ FALTANTE'}</li>
                          <li><strong>titulo:</strong> {item.data.titulo || '❌ FALTANTE'}</li>
                          <li><strong>activo:</strong> {item.data.activo ? '✅ true' : '❌ false'}</li>
                        </ul>

                        <h6 className="text-success">💰 Precios</h6>
                        <ul className="list-unstyled small">
                          <li><strong>precio_actual_usd:</strong> ${item.data.precio_actual_usd || 0}</li>
                          <li><strong>precio_actual_mxn:</strong> ${item.data.precio_actual_mxn || 0}</li>
                        </ul>
                      </div>
                      
                      <div className="col-md-6">
                        <h6 className="text-danger">🔗 Enlaces Críticos</h6>
                        <ul className="list-unstyled small">
                          <li>
                            <strong>asesor_id:</strong> 
                            {item.data.asesor_id && item.data.asesor_id.length > 0 
                              ? `✅ [${item.data.asesor_id.join(', ')}]` 
                              : '❌ VACÍO - Por eso no aparecen propiedades en perfiles de asesores'}
                          </li>
                          <li>
                            <strong>asociado_id:</strong> 
                            {item.data.asociado_id && item.data.asociado_id.length > 0 
                              ? `✅ [${item.data.asociado_id.join(', ')}]` 
                              : '⚠️ VACÍO'}
                          </li>
                          <li>
                            <strong>agencia_info.inmobiliaria_id:</strong> 
                            {item.data.agencia_info?.inmobiliaria_id 
                              ? `✅ ${item.data.agencia_info.inmobiliaria_id}` 
                              : '❌ FALTANTE'}
                          </li>
                          <li>
                            <strong>agencia_info.agente_listador:</strong> 
                            {item.data.agencia_info?.agente_listador 
                              ? `✅ ${item.data.agencia_info.agente_listador}` 
                              : '❌ FALTANTE'}
                          </li>
                        </ul>

                        <h6 className="text-info">📷 Multimedia</h6>
                        <ul className="list-unstyled small">
                          <li>
                            <strong>fotos:</strong> 
                            {item.data.fotos && item.data.fotos.length > 0 
                              ? `✅ ${item.data.fotos.length} foto(s)` 
                              : '⚠️ Sin fotos'}
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <details>
                        <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                          Ver datos completos (JSON)
                        </summary>
                        <pre className="small mt-2 bg-light p-2 rounded" style={{ maxHeight: '200px', overflow: 'auto' }}>
                          {JSON.stringify(item.data, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </div>
                </div>
              ))}

              <div className="alert alert-warning mt-4">
                <h6>🔧 Diagnóstico del Problema</h6>
                <p>
                  Si los arrays <code>asesor_id</code> están vacíos, significa que durante la migración 
                  no se encontraron coincidencias entre los nombres en el CSV y los asesores en la base de datos.
                </p>
                <p>
                  <strong>Posibles causas:</strong>
                </p>
                <ul className="mb-0">
                  <li>El campo "Listing Agent" en el CSV no coincide exactamente con los nombres en la colección "asesor"</li>
                  <li>Los asesores se migraron después de las propiedades</li>
                  <li>El matching fuzzy no funcionó para algunos nombres</li>
                </ul>
              </div>

              <div className="alert alert-info">
                <h6>💡 Solución Recomendada</h6>
                <p>
                  1. Verificar que existan asesores con nombres que coincidan con "Listing Agent" del CSV<br/>
                  2. Si hay desajustes, re-ejecutar solo la migración de propiedades<br/>
                  3. O crear un script de corrección post-migración para vincular propiedades existentes
                </p>
                <a href="/ampi-migration-v2" className="btn btn-primary">
                  <i className="fa-solid fa-tools me-2"></i>
                  Ir a Sistema de Migración
                </a>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}